import React from "react";

// Helper function to parse compound spacing values
const parseSpacing = (spacing) => {
  // Support two formats:
  // 1. "top_none_bottom_comfortable" (new format)
  // 2. "comfortable_top_tight_bottom" (existing format)

  if (spacing.startsWith("top_") && spacing.includes("_bottom_")) {
    // Format: "top_none_bottom_comfortable"
    const match = spacing.match(/^top_(.+)_bottom_(.+)$/);
    if (match) {
      return {
        isCompound: true,
        top: match[1],
        bottom: match[2],
      };
    }
  } else if (spacing.includes("_top_") && spacing.includes("_bottom")) {
    // Format: "comfortable_top_tight_bottom"
    const match = spacing.match(/^(.+)_top_(.+)_bottom$/);
    if (match) {
      return {
        isCompound: true,
        top: match[1],
        bottom: match[2],
      };
    }
  }
  return { isCompound: false, spacing };
};

// Individual spacing classes
const spacingTopClasses = {
  compact: "pt-8 sm:pt-12",
  comfortable: "pt-16 sm:pt-24",
  spacious: "pt-24 sm:pt-40",
  tight: "pt-4 sm:pt-6",
  none: "",
};

const spacingBottomClasses = {
  compact: "pb-8 sm:pb-12",
  comfortable: "pb-16 sm:pb-24",
  spacious: "pb-24 sm:pb-40",
  tight: "pb-4 sm:pb-6",
  none: "",
};

// Section - Handles section-level layout (backgrounds, vertical spacing)
export const Section = ({
  children,
  variant = "default",
  spacing = "comfortable",
  margin = true,
  className = "",
  ...props
}) => {
  const variants = {
    default: "bg-white",
    primary: "bg-[var(--color-primary)]",
    secondary: "bg-[var(--color-secondary-dark)]",
    secondaryDark: "bg-[var(--color-secondary-dark)]",
    white: "bg-gray-100",
    dark: "bg-[var(--color-dark-gray)]",
    transparent: "bg-transparent",
  };

  const spacingClasses = {
    tight: "sm:py-3",
    compact: "py-8 sm:py-12",
    comfortable: "py-16 sm:py-24",
    spacious: "py-24 sm:py-40",
    none: "",
  };

  const parsedSpacing = parseSpacing(spacing);

  // Build custom spacing class if compound spacing is used
  const customSpacingClass = parsedSpacing.isCompound
    ? `${spacingTopClasses[parsedSpacing.top] || ""} ${
        spacingBottomClasses[parsedSpacing.bottom] || ""
      }`
    : "";

  const finalSpacingClass = parsedSpacing.isCompound
    ? customSpacingClass
    : spacingClasses[spacing] || "";

  // const marginClass = margin ? "my-2 sm:my-3" : "";
  const marginClass = margin ? "my-2 sm:my-3" : "";
  return (
    <section
      className={`${variants[variant]} ${finalSpacingClass} ${marginClass} ${className}`}
      {...props}
    >
      {children}
    </section>
  );
};

// ContentBlock - Handles content-level layout (max-width, horizontal spacing, typography)
export const ContentBlock = ({
  children,
  maxWidth = "4xl",
  spacing = "comfortable",
  align = "left",
  margin = true,
  className = "",
  ...props
}) => {
  const maxWidthClasses = {
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    full: "max-w-full",
    none: "",
  };

  const spacingClasses = {
    tight: "px-2 sm:px-3",
    comfortable: "px-4 sm:px-6 lg:px-0",
    spacious: "px-6 sm:px-8 lg:px-12",
    none: "",
  };

  const alignClasses = {
    left: "text-left",
    center: "text-center mx-auto",
    right: "text-right ml-auto",
  };

  // Apply spacing only if margin is true
  const finalSpacing = margin ? spacingClasses[spacing] : "";

  return (
    <div
      className={`${maxWidthClasses[maxWidth]} ${finalSpacing} ${alignClasses[align]} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

// Container - Simple wrapper for full-width containers (replaces content-container)
export const Container = ({
  children,
  maxWidth = "7xl",
  noPaddingMobile = false,
  className = "",
  ...props
}) => {
  const maxWidthClasses = {
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    "8xl": "max-w-8xl",
    "9xl": "max-w-9xl",
    full: "max-w-full",
  };

  const paddingClasses = noPaddingMobile
    ? "sm:px-6 lg:px-0"
    : "px-4 sm:px-6 lg:px-0";

  return (
    <div
      className={`${maxWidthClasses[maxWidth]} mx-auto ${paddingClasses} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};
